import { TuiHint, TuiInput } from '@taiga-ui/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
} from '@angular/core';

import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TuiFileLike, TuiFiles } from '@taiga-ui/kit';
import { WorkflowFormFieldInputFiles } from '@features/requests/request-form/components/request-form-workflows/types';
import {
  getWorkflowFileConstraints,
  getWorkflowControlFiles,
  getWorkflowFileOrigin,
  toWorkflowControlValue,
} from '@core/services/workflow';

@Component({
  selector: 'x-input-files-field',
  imports: [ReactiveFormsModule, TuiFiles, TuiHint, TuiInput],
  templateUrl: './input-files-field.component.html',
  styleUrls: ['./input-files-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class InputFilesFieldComponent {
  field = input.required<WorkflowFormFieldInputFiles>();
  /** В одиночном режиме (maxFilesCount: 1) Taiga хранит один File, иначе — массив */
  control = input.required<FormControl<File | File[] | null>>();
  /** Файлы, которые не удалось сохранить на сервере, с причиной */
  failedFiles = input<ReadonlyMap<File, string>>(new Map());

  rejectedFiles = signal<TuiFileLike[]>([]);

  /** Ограничения из схемы: accept, размер, количество (как на backend) */
  protected readonly $constraints = computed(() =>
    getWorkflowFileConstraints(this.field()),
  );
  /** Без maxFilesCount количество не ограничено */
  protected readonly $multiple = computed(
    () => (this.$constraints().maxFilesCount ?? Infinity) > 1,
  );
  protected readonly $failedFileLikes = computed(
    () =>
      new Map<File, TuiFileLike>(
        [...this.failedFiles()].map(([file, message]) => [
          file,
          {
            name: file.name,
            size: file.size,
            type: file.type,
            content: message,
          },
        ]),
      ),
  );

  /** Файлы поля независимо от режима: одиночный File или массив */
  protected getFiles(): File[] {
    return getWorkflowControlFiles(this.control().value);
  }

  removeFile(file: File): void {
    this.setFiles(this.getFiles().filter(f => f !== file));
    this.control().markAsDirty();
  }

  onReject(files: TuiFileLike | readonly TuiFileLike[]): void {
    const rejected = Array.isArray(files) ? files : [files];
    // Taiga оставляет отклонённые файлы в значении контрола: убираем новые файлы,
    // чтобы они не показывались дважды и не уходили на сервер при сохранении.
    // Файлы, уже сохранённые на сервере или в очереди, не трогаем
    const rejectedNew = new Set<TuiFileLike>(
      rejected.filter(
        file => file instanceof File && !getWorkflowFileOrigin(file),
      ),
    );
    const currentFiles = this.getFiles();

    if (currentFiles.some(file => rejectedNew.has(file))) {
      this.setFiles(currentFiles.filter(file => !rejectedNew.has(file)));
    }

    this.rejectedFiles.set([...this.rejectedFiles(), ...rejected]);
  }

  /** Записать файлы в формате режима поля: одиночный File или массив */
  private setFiles(files: File[]): void {
    this.control().setValue(toWorkflowControlValue(files, this.$multiple()));
  }

  clearRejected(file: TuiFileLike): void {
    this.rejectedFiles.set(this.rejectedFiles().filter(f => f !== file));
  }
}
